import { motion } from "framer-motion";
import {
  ShoppingCart,
  Wallet,
  Flame,
  Boxes,
  ChefHat,
  Bike,
  CalendarCheck2,
  LineChart,
  Smartphone,
  Globe,
  QrCode,
  Receipt,
  Truck,
  Clock4,
  TrendingUp,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Phone,
  ShieldCheck,
  Zap,
  Target,
  Layers,
} from "lucide-react";
import SlideShell from "@/components/SlideShell";
import Reveal from "@/components/Reveal";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, WEBSITE_URL } from "@/data/constants";

/* ============ SHARED ATOMS ============ */

const Eyebrow = ({ children, variant = "dark" }) => (
  <div
    className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold ${
      variant === "light" ? "text-orange-300" : "text-emerald-800"
    }`}
  >
    <span
      className={`block w-6 h-px ${variant === "light" ? "bg-orange-300" : "bg-emerald-800"}`}
    />
    {children}
  </div>
);

const PrimaryButton = ({ children, href = "#", testId, onClick }) => (
  <motion.a
    whileHover={{ y: -2 }}
    whileTap={{ scale: 0.96 }}
    href={href}
    onClick={onClick}
    data-testid={testId}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
    className="group inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-6 py-3.5 transition-colors shadow-[0_8px_30px_-8px_rgba(249,115,22,0.6)]"
  >
    {children}
    <ArrowRight
      className="w-4 h-4 -mr-1 transition-transform group-hover:translate-x-1"
    />
  </motion.a>
);

const SecondaryButton = ({ children, href = "#", testId, onClick, variant = "dark" }) => (
  <motion.a
    whileHover={{ y: -2 }}
    whileTap={{ scale: 0.96 }}
    href={href}
    onClick={onClick}
    data-testid={testId}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
    className={`inline-flex items-center gap-2 rounded-full font-semibold text-sm px-6 py-3.5 transition-colors ${
      variant === "light"
        ? "border border-white/25 text-white hover:bg-white hover:text-zinc-950"
        : "border border-zinc-900/15 text-zinc-950 hover:bg-zinc-950 hover:text-white"
    }`}
  >
    {children}
  </motion.a>
);

const ModuleChip = ({ icon: Icon, label }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 ring-1 ring-white/15 text-white/90 text-xs font-medium">
    <Icon className="w-3.5 h-3.5 text-orange-400" />
    {label}
  </div>
);

/* ============ SLIDE 1 — PORTADA ============ */

const HERO_BG =
  "https://images.unsplash.com/photo-1771574207939-ba1364dcdb21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXN0YXVyYW50JTIwaW50ZXJpb3IlMjBjaGVmJTIwcGxhdGluZ3xlbnwwfHx8fDE3ODE4MDAzOTF8MA&ixlib=rb-4.1.0&q=85";

const Portada = ({ id, index, total, goTo }) => (
  <SlideShell id={id} index={index} total={total} variant="light" className="bg-zinc-950 text-white">
    {/* Background image with parallax-ready container */}
    <motion.div
      initial={{ scale: 1.08, opacity: 0.6 }}
      whileInView={{ scale: 1, opacity: 1 }}
      viewport={{ once: false, amount: 0.4 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0"
      style={{
        backgroundImage: `url('${HERO_BG}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-950/80 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />

    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20 md:pb-28 pt-32">
      <Reveal delay={0.1}>
        <Eyebrow variant="light">Restaurant Operating System</Eyebrow>
      </Reveal>
      <Reveal delay={0.2} y={40}>
        <h1 className="font-display font-black mt-5 leading-[0.92] tracking-tighter text-[14vw] sm:text-[10vw] lg:text-[7.5vw] xl:text-[120px] text-white">
          ERP <span className="text-orange-400">Alimentos</span>
        </h1>
      </Reveal>
      <Reveal delay={0.35}>
        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed">
          Sistema integral para restaurantes, bares, cafeterías y negocios
          gastronómicos. Administra tu restaurante desde una sola plataforma{" "}
          <span className="text-orange-300 font-semibold">100% en la nube</span>.
        </p>
      </Reveal>
      <Reveal delay={0.5}>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <PrimaryButton
            href={WHATSAPP_URL}
            testId="cta-portada-info"
          >
            Solicitar información
          </PrimaryButton>
          <SecondaryButton
            variant="light"
            testId="cta-portada-modulos"
            onClick={(e) => {
              e.preventDefault();
              goTo(3);
            }}
          >
            Ver módulos
          </SecondaryButton>
        </div>
      </Reveal>

      <Reveal delay={0.7}>
        <div className="mt-12 flex flex-wrap gap-2.5">
          {[
            { icon: ShoppingCart, label: "Punto de venta" },
            { icon: Flame, label: "Cocina en tiempo real" },
            { icon: QrCode, label: "Menú QR" },
            { icon: LineChart, label: "Reportes en vivo" },
            { icon: Bike, label: "Delivery propio" },
          ].map((m) => (
            <ModuleChip key={m.label} {...m} />
          ))}
        </div>
      </Reveal>
    </div>

    {/* scroll hint */}
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/50 text-[10px] uppercase tracking-[0.3em] flex flex-col items-center gap-2">
      <motion.span
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        Scroll
      </motion.span>
      <span className="block w-px h-8 bg-white/30" />
    </div>
  </SlideShell>
);

/* ============ SLIDE 2 — ¿QUÉ ES? ============ */

const QueEs = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} className="bg-white">
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <Reveal><Eyebrow>02 · La plataforma</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-black mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-zinc-950">
            Todo tu restaurante,{" "}
            <span className="text-emerald-800">conectado</span> en una sola herramienta.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 text-zinc-600 text-base md:text-lg leading-relaxed max-w-xl">
            ERP Alimentos es una plataforma en la nube diseñada para{" "}
            <span className="text-zinc-950 font-semibold">digitalizar, controlar y automatizar</span>{" "}
            la operación diaria de restaurantes, bares, cafeterías, dark
            kitchens y negocios gastronómicos.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 flex flex-wrap gap-4 text-sm text-zinc-700">
            {["100% en la nube", "Multi-sucursal", "Sin instalaciones"].map((t) => (
              <div key={t} className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                {t}
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-6">
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {[
            { icon: LineChart, label: "Dashboard", tint: "bg-emerald-900 text-white" },
            { icon: ShoppingCart, label: "POS", tint: "bg-orange-500 text-white" },
            { icon: QrCode, label: "Menú QR", tint: "bg-zinc-100 text-zinc-900" },
            { icon: ChefHat, label: "Cocina", tint: "bg-zinc-950 text-white" },
            { icon: Boxes, label: "Inventario", tint: "bg-emerald-50 text-emerald-900" },
            { icon: Receipt, label: "Reportes", tint: "bg-orange-50 text-orange-700" },
          ].map((m, i) => (
            <Reveal key={m.label} delay={0.1 + i * 0.06}>
              <div
                className={`relative aspect-square rounded-2xl p-5 flex flex-col justify-between overflow-hidden ${m.tint}`}
              >
                <m.icon className="w-7 h-7" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] opacity-60">
                    Módulo
                  </div>
                  <div className="font-display font-bold text-lg mt-1">{m.label}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </SlideShell>
);

/* ============ SLIDE 3 — OBJETIVO ============ */

const Objetivo = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} variant="light" className="bg-emerald-950 text-white noise">
    <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-orange-500/20 blur-3xl" />
    <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl" />

    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20">
      <Reveal><Eyebrow variant="light">03 · Objetivo</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display font-black mt-5 text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tighter max-w-4xl">
          Vende más. Controla mejor.{" "}
          <span className="text-orange-400">Decide con datos.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
          ERP Alimentos ayuda a los negocios gastronómicos a vender más,
          controlar mejor sus operaciones, reducir errores, mejorar la atención
          al cliente y tomar decisiones con información en tiempo real.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 rounded-2xl overflow-hidden ring-1 ring-white/10">
        {[
          { icon: ShieldCheck, label: "Más control" },
          { icon: Target, label: "Menos errores" },
          { icon: Zap, label: "Atención veloz" },
          { icon: Layers, label: "Mejor administración" },
          { icon: TrendingUp, label: "Reducción de costos" },
          { icon: Sparkles, label: "Más rentabilidad" },
        ].map((b, i) => (
          <Reveal key={b.label} delay={0.1 + i * 0.08}>
            <div className="bg-emerald-950 p-6 md:p-7 h-full flex flex-col gap-4">
              <b.icon className="w-6 h-6 text-orange-400" />
              <div className="font-display font-semibold text-base md:text-lg leading-tight">
                {b.label}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </SlideShell>
);

/* ============ SLIDE 4 — CONTENIDO / MÓDULOS ============ */

const MODULES_LIST = [
  { icon: ShoppingCart, label: "Ventas", desc: "Mesas, mostrador y delivery." },
  { icon: Wallet, label: "Caja", desc: "Cortes y arqueos automáticos." },
  { icon: Flame, label: "Cocina", desc: "Comandas en tiempo real." },
  { icon: Boxes, label: "Inventario", desc: "Existencias y movimientos." },
  { icon: ChefHat, label: "Recetas", desc: "Costeo y margen real." },
  { icon: Bike, label: "Delivery", desc: "Sin comisiones de terceros." },
  { icon: CalendarCheck2, label: "Reservas", desc: "Confirma online." },
  { icon: LineChart, label: "Reportes", desc: "Datos en vivo." },
  { icon: Smartphone, label: "Menú digital", desc: "QR sin reimpresión." },
  { icon: Globe, label: "Página web", desc: "Pedidos directos." },
];

const Contenido = ({ id, index, total }) => (
  <SlideShell id={id} index={index} total={total} className="bg-zinc-100">
    <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-20">
      <div className="grid lg:grid-cols-12 items-end gap-8 mb-10">
        <div className="lg:col-span-7">
          <Reveal><Eyebrow>04 · Módulos</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter text-zinc-950">
              Una plataforma{" "}
              <span className="text-orange-500">completa</span> para tu negocio.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <p className="text-zinc-600 text-base md:text-lg leading-relaxed">
              ERP Alimentos integra las herramientas esenciales para operar tu
              restaurante de forma profesional.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {MODULES_LIST.map((m, i) => (
          <Reveal key={m.label} delay={0.05 * i}>
            <motion.div
              whileHover={{ y: -4 }}
              className="group h-full bg-white rounded-2xl p-5 md:p-6 border border-zinc-200/80 hover:border-emerald-700/40 hover:shadow-[0_10px_40px_-10px_rgba(6,78,59,0.25)] transition-all"
              data-testid={`module-card-${m.label.toLowerCase()}`}
            >
              <div className="w-10 h-10 grid place-items-center rounded-xl bg-emerald-50 text-emerald-900 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                <m.icon className="w-5 h-5" />
              </div>
              <div className="mt-5 font-display font-bold text-zinc-950 text-base md:text-lg">
                {m.label}
              </div>
              <div className="mt-1 text-xs md:text-sm text-zinc-500 leading-relaxed">
                {m.desc}
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </div>
  </SlideShell>
);

export const SLIDES_PART_A = { portada: Portada, "que-es": QueEs, objetivo: Objetivo, contenido: Contenido };
